/**
 * src/lib/posthog.ts
 * PostHog Free Cloud — D-30 (researcher fiat) lazy SDK loader.
 * Mirrors src/lib/consent.ts loadClarity pattern (Phase 2 D-21).
 *
 * NEVER initialize at app bootstrap. Always called from consent subscriber
 * gated on prefs.analytics === true (Pitfall N — LGPD).
 *
 * P5a (CRO perf): posthog-js agora entra via dynamic import() — a lib
 * (~150-180KB min) sai do chunk inicial e só baixa APÓS consent.
 */
import { getOrCreateSessionId } from "./experiments";

declare global {
  interface Window {
    __posthogLoaded?: boolean;
  }
}

/**
 * D-30 — Lazy-injects PostHog SDK post-consent.
 * Idempotente: window.__posthogLoaded guard prevents re-initialization
 * (setado ANTES do await — evita corrida com chamadas concorrentes).
 * No-op without a real API key (placeholder or empty key).
 * persistence: "localStorage" — LGPD-light, no extra cookies (D-25 pattern).
 * bootstrap.distinctID: aligns PostHog identity with D-32 session_id.
 */
export async function loadPostHog(): Promise<void> {
  if (typeof window === "undefined") return;
  if (window.__posthogLoaded) return;

  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
  if (!apiKey || apiKey === "" || apiKey.startsWith("phc_XXXX")) return; // No-op without real key

  window.__posthogLoaded = true; // antes do await — idempotência sob concorrência

  const { default: posthog } = await import("posthog-js");
  posthog.init(apiKey, {
    api_host: "https://us.i.posthog.com",
    autocapture: false, // Only flags + custom events; GA4 handles pageview
    capture_pageview: false,
    persistence: "localStorage", // No extra cookies — LGPD-light (D-25 pattern)
    bootstrap: { distinctID: getOrCreateSessionId() }, // Align with D-32 session_id
  });
}
